import { labelFor } from '../shared/format';
import { webUrlOf } from './client/clientFacts';

/**
 * The connected tools that live inside this app, by canonical key. A chip for one of
 * these always opens the app's own page, whatever address the backend sent: the page
 * exists here, so a guessed, stale or relative address cannot send anyone to a broken one.
 * Labels follow the sidebar.
 */
export const APP_TOOLS = {
  aim: { label: 'AI Visibility', path: '/ai-visibility' },
  cdp: { label: 'Visitor Intelligence', path: '/visitors' },
  ga4: { label: 'Website Analytics', path: '/google-analytics' },
};

/** Other spellings of a tool key the backend may use, as normalised keys. */
const TOOL_ALIASES = {
  ai_visibility: 'aim',
  ai_monitor: 'aim',
  visitors: 'cdp',
  visitor_intelligence: 'cdp',
  ga: 'ga4',
  google_analytics: 'ga4',
  analytics: 'ga4',
  website_analytics: 'ga4',
};

/** "Ai Visibility" / "ai-visibility" / "AI_VISIBILITY" → "ai_visibility". */
const normaliseKey = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/** The canonical tool key for a destination key, or null when it is not one of the app's tools. */
export const toolKeyOf = (key) => {
  const k = normaliseKey(key);
  const canonical = TOOL_ALIASES[k] || k;
  return Object.prototype.hasOwnProperty.call(APP_TOOLS, canonical) ? canonical : null;
};

const BARE_DOMAIN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#]\S*)?$/i;

/**
 * An address as an absolute http(s) URL, or null. A bare domain ("example.com") gets
 * https://; a relative path, a `javascript:` or `data:` value, a placeholder and anything
 * with whitespace is rejected rather than rendered as a link that goes nowhere.
 * @param {*} value
 * @returns {string|null}
 */
export const validWebUrl = (value) => {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return webUrlOf(BARE_DOMAIN.test(v) ? `https://${v}` : v);
};

/**
 * The Elsewhere chips, resolved: known tools become the app's own page, any other
 * destination stays only if its value is a valid web address. Accepts the backend's
 * `elsewhere` either as `{ key: url | { url | href } }` or `[{ key | label | name, url | href }]`.
 * The first chip for a tool wins.
 *
 * @param {Array|Record<string,*>|null|undefined} elsewhere
 * @returns {Array<{key: string, label: string, url: string}>}
 */
export function resolveElsewhere(elsewhere) {
  const entries = Array.isArray(elsewhere)
    ? elsewhere.filter((x) => x && typeof x === 'object').map((x) => ({
        key: x.key || x.label || x.name || '',
        label: x.label || x.name || null,
        raw: x.url || x.href,
      }))
    : Object.entries(elsewhere && typeof elsewhere === 'object' ? elsewhere : {}).map(([k, v]) => ({
        key: k,
        label: null,
        raw: typeof v === 'string' ? v : v?.url || v?.href,
      }));

  const seen = new Set();
  const out = [];
  for (const { key, label, raw } of entries) {
    const tool = toolKeyOf(key);
    // A tool chip is built from the app's own route, so it does not depend on `raw` at all.
    // `raw` only has to be present, because the backend not returning a tool means this
    // client does not have it.
    const present = raw != null && String(raw).trim() !== '';
    const url = tool ? (present ? APP_TOOLS[tool].path : null) : validWebUrl(raw);
    if (!url) continue;
    const id = tool || normaliseKey(key) || url;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ key: id, label: tool ? APP_TOOLS[tool].label : (label || labelFor(String(key).replace(/-/g, ' ')) || 'Open'), url });
  }
  return out;
}
